import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Monitor, FileText, History as HistoryIcon, Plus, X, Edit2, Tag, MessageCircle, ChevronDown, Copy, Check, Building2, DollarSign, ListChecks, Clock } from 'lucide-react';
import { Visitor, Chat, Note } from './agent-console';
import { AttributePill, SystemChip, TagPill, ActionChip } from './crm-grid/pills';
import { ValueSheet } from './crm-grid/ValueSheet';
import { ConfirmDialog } from './crm-grid/ConfirmDialog';
import { optionToken, optClasses } from './crm-grid/optionColor';

/**
 * The Lead/Visitor detail panel (PRD docs/LEAD-DETAIL-PANEL-PRD.md, design
 * docs/LEAD-PANEL-DESIGN-DIRECTION.md). ONE SUBJECT: `personId` (a visitor token or a lead id) is
 * resolved server-side to its Lead by IDENTITY (GET /api/customers/:id/detail →
 * LeadService.getLeadDetail → resolveLeadId), never by live-roster presence — the same person renders
 * the same durable record online or offline. `visitor` is OPTIONAL, presence-derived ENRICHMENT
 * (current page, navigation history, device, IP) layered on top when a live session exists; it is
 * never the source of durable fields (name/contact/tags/CRM state), which is what let an offline lead
 * render blank before (F1/F2 union, now removed at both mount points in agent-console.tsx).
 *
 * Hoisted to module scope: defining subcomponents inside CustomerDetails gave them a fresh function
 * identity on every render, so React unmounted+remounted their subtree each keystroke — detaching the
 * contact-edit inputs mid-edit (F2). Stable identity = no remount.
 */

interface CustomFieldValue {
    fieldDefId: string;
    key: string;
    label: string;
    type: string;
    value: string | null;
}

interface LeadDetail {
    leadId: string;
    name: string | null;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phone: string | null;
    qualified: boolean;
    qualifiedReason: string | null;
    qualifiedAt: string | null;
    clientStatus: string | null;
    qualificationLevel: string | null;
    savedAt: string | null;
    suppressedAt: string | null;
    embedOrigin: string | null;
    firstSeenAt: string;
    // LAST-KNOWN session facts (PM, AC1 resolution): persisted on the Lead cumulatively (enrich()
    // never clears an earlier value), so they OUTLIVE the session that produced them — provenance-free,
    // never dated. Shown offline, hedged ("Last known: …"), never presented as a CURRENT fact — that is
    // the live roster's job (`visitor` below).
    location?: { city?: string; countryCode?: string } | null;
    userAgent?: { browser?: string; version?: string; os?: string; platform?: string } | null;
    tags: string[];
    alternateContacts: Array<{ kind: string; value: string; fromLeadId?: string }>;
    mergeLog: Array<{ fromLeadId: string; broughtIn?: { conversations: number; tags: number; alternateContacts: number }; listedBefore?: boolean; listedAfter?: boolean; undone?: boolean }>;
    conversationsCount: number;
    lastConversationAt: string | null;
    customFields: CustomFieldValue[];
    // #62 Contacts depth spec §3 DETAIL: "the lead panel...plus the new relations: company · deals ·
    // tasks · activity timeline" — reusing THIS panel rather than a second one.
    kind?: string;
    company?: { id: string; name: string } | null;
    deals?: Array<{ id: string; name: string; value: number | null; stageName: string | null }>;
    tasks?: Array<{ id: string; title: string; status: string; completedAt: string | null }>;
    recentActivity?: Array<{ id: string; type: string; summary: string; occurredAt: string }>;
}

function formatDate(iso?: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// Month+year only (no day) — the header's age-of-record line ("Lead Since Aug 2026"), matching the
// original component's coarser precision there. formatDate's day-level precision is for everything
// else (qualifiedAt, savedAt, suppressedAt, notes, conversations), which is a separate, deliberate choice.
function formatMonthYear(iso?: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

// #50 (cmdr/DevOps, 2026-08-23): the TWO invariants are in direct tension — clearing `detail` to null
// on a personId change is what caused the mid-click DETACH (the whole `{detail && (...)}` subtree
// unmounts), but simply NOT clearing it is what causes the WRONG-PERSON-FLASH the original :160-169
// reset existed to prevent (A's data staying on screen, readable, once B is selected — a scrim/
// pointer-events-none guard only blocks INTERACTION, not READING, so it does not actually satisfy
// "never shows"). The fix that threads both: reset IDENTITY synchronously to an honest EMPTY shell for
// the NEW personId (never null) rather than to null — `detail` stays TRUTHY throughout, so the
// `{detail && (...)}` subtree NEVER structurally unmounts (no detach), while every field inside it
// reads as absent/"—" via the SAME honest-absence rendering this codebase already uses everywhere
// (no separate loading-only code path to keep in sync). `leadId` is stamped with the CURRENT personId,
// so identity-derived values (the avatar colour) are correct immediately, never mismatched.
function emptyDetail(leadId: string): LeadDetail {
    return {
        leadId, name: null, firstName: null, lastName: null, email: null, phone: null,
        qualified: false, qualifiedReason: null, qualifiedAt: null,
        clientStatus: null, qualificationLevel: null, savedAt: null, suppressedAt: null,
        embedOrigin: null, firstSeenAt: '', location: null, userAgent: null,
        tags: [], alternateContacts: [], mergeLog: [], conversationsCount: 0, lastConversationAt: null,
        customFields: [], kind: undefined, company: null, deals: [], tasks: [], recentActivity: [],
    };
}

const Card: React.FC<{ title: string; icon?: React.ReactNode; action?: React.ReactNode; testId?: string; children: React.ReactNode }> = ({ title, icon, action, testId, children }) => (
    <div data-testid={testId} className="rounded-xl border border-border-neutral bg-surface p-4">
        <div className="flex items-center justify-between gap-2 mb-3 pb-3 border-b border-border-neutral">
            <div className="flex items-center gap-2 min-w-0">
                {icon && <span className="text-text-muted shrink-0">{icon}</span>}
                <h3 className="text-sm font-semibold text-text-strong truncate">{title}</h3>
            </div>
            {action}
        </div>
        {children}
    </div>
);

// break-words: AC-D9 (F-LONG) — a long email or full international phone has no natural break
// point, so without it the value overflows the panel's fixed CSS width instead of wrapping.
const Row: React.FC<{ label: string; testId?: string; children: React.ReactNode }> = ({ label, testId, children }) => (
    <div className="flex flex-col gap-0.5 py-1.5 min-w-0" data-testid={testId}>
        <span className="text-[11px] font-medium text-text-muted">{label}</span>
        <span className="text-sm text-text-strong break-words">{children}</span>
    </div>
);

const PanelSkeleton: React.FC = () => (
    <div className="flex flex-col gap-4 animate-pulse" aria-hidden data-testid="lead-detail-skeleton">
        <div className="rounded-xl border border-border-neutral bg-surface p-4 flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-canvas" />
            <div className="flex-1 space-y-2">
                <div className="h-4 w-1/2 rounded bg-canvas" />
                <div className="h-3 w-1/3 rounded bg-canvas" />
            </div>
        </div>
        {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-xl border border-border-neutral bg-surface p-4 space-y-3">
                <div className="h-3 w-1/4 rounded bg-canvas" />
                <div className="h-3 w-full rounded bg-canvas" />
                <div className="h-3 w-2/3 rounded bg-canvas" />
            </div>
        ))}
    </div>
);

interface CustomerDetailsProps {
    personId: string;
    visitor?: Visitor;
    chat: Chat;
    onViewPastChat: (chat: any) => void;
    onUpdateContact: (contact: { firstName?: string, lastName?: string, phone?: string, email?: string }) => void;
    onUpdateTags: (tags: string[]) => void;
    onAddNote: (content: string) => Promise<Note>;
    notes: Note[];
    // #62 Contacts depth spec §3 DELETE — optional: only Contacts/Leads screens offer contact-delete
    // (the WA conversation details pane mounts this same component for a live thread and should not).
    // Called after a successful delete so the caller can deselect/close its own panel slot.
    onDeleted?: () => void;
    /** B5 — fired after convert LANDS so the host can bump both lists' refresh signal. */
    onConverted?: () => void;
}

const FIELD_BUDGET = 5;

export function CustomerDetails({ personId, visitor, chat, onViewPastChat, onUpdateContact, onUpdateTags, onAddNote, notes, onDeleted, onConverted }: CustomerDetailsProps) {
    const { t } = useTranslation('common');
    const [detail, setDetail] = useState<LeadDetail | null>(null);
    // #50: true once ANY detail has ever loaded for THIS mounted instance — distinguishes the true
    // first mount (still shows PanelSkeleton, nothing to preserve) from a later personId switch
    // (resets to emptyDetail(personId) instead, so content never structurally unmounts).
    const hasLoadedRef = useRef(false);
    const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
    const [retryTick, setRetryTick] = useState(0);
    const [convertFailed, setConvertFailed] = useState(false); // B5 — a failed convert is VISIBLE (A3)

    const [isEditingContact, setIsEditingContact] = useState(false);
    const [contactForm, setContactForm] = useState({ firstName: '', lastName: '', email: '', phone: '' });
    const [newNote, setNewNote] = useState('');
    const [isAddingNote, setIsAddingNote] = useState(false);
    const [tagInput, setTagInput] = useState('');
    const [isAddingTag, setIsAddingTag] = useState(false);
    const [editingAttr, setEditingAttr] = useState<'clientStatus' | 'qualificationLevel' | null>(null);
    const [showAllFields, setShowAllFields] = useState(false);
    const [showProvenance, setShowProvenance] = useState(false);
    const [isUndoingMerge, setIsUndoingMerge] = useState(false);
    const [copiedId, setCopiedId] = useState(false);

    // ONE fetch, keyed on identity (personId) — never on live presence.
    //
    // 🔴 #50 fix (cmdr/DevOps, 2026-08-23) — the two invariants are in DIRECT TENSION, not independent:
    //   1. No mid-click element-detach on in-session :id nav — content must never structurally unmount
    //      just because personId changed (a prior `setDetail(null)` caused exactly this).
    //   2. No wrong-person-flash — person A's content must never be READABLE as if it were B's (the
    //      ORIGINAL reason for that null-out). A scrim/pointer-events-none guard only blocks
    //      INTERACTION, not READING — an operator can still SEE A's phone number under B's id, which
    //      does not satisfy "never shows." Removing the null-out to fix (1) is exactly what causes (2).
    // The fix that threads both: on a personId CHANGE (not the first-ever load), reset `detail`
    // SYNCHRONOUSLY to `emptyDetail(personId)` — an honest, all-absent shell for the NEW id, never
    // `null`. `detail` stays TRUTHY the whole time, so `{detail && (...)}` never structurally unmounts
    // (no detach) — but every field the render shows reads as genuinely absent ("—"), so A's data is
    // gone from the screen the SAME tick, not just dimmed (no flash). `hasLoadedRef` distinguishes this
    // from the true first mount, which still shows PanelSkeleton (nothing to preserve continuity with).
    useEffect(() => {
        let alive = true;
        setLoadState('loading');
        if (hasLoadedRef.current) setDetail(emptyDetail(personId));
        fetch(`/api/customers/${encodeURIComponent(personId)}/detail`)
            .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
            .then((data: LeadDetail) => { if (alive) { setDetail(data); setLoadState('ready'); hasLoadedRef.current = true; } })
            .catch(() => {
                // A revalidation failure: leave the empty shell in place (honest "we don't have this"),
                // same display as a genuine first-load failure would show via the fields themselves —
                // no separate error banner needed here since nothing is left un-refreshed/actionable.
                if (alive) setLoadState('error');
            });
        return () => { alive = false; };
    }, [personId, retryTick]);

    // Contact form seeds from the DURABLE record (detail), never from `visitor` — visitor is undefined
    // offline, and seeding from it was the F1/F2 bug (an offline lead's own saved contact looked empty).
    useEffect(() => {
        if (!detail) return;
        setContactForm({
            firstName: detail.firstName || '',
            lastName: detail.lastName || '',
            email: detail.email || '',
            phone: detail.phone || '',
        });
    }, [detail]);

    const handleContactSave = () => {
        onUpdateContact(contactForm);
        setDetail((d) => (d ? { ...d, ...contactForm } : d));
        setIsEditingContact(false);
    };

    const handleNoteSubmit = async () => {
        if (!newNote.trim()) return;
        setIsAddingNote(true);
        await onAddNote(newNote);
        setNewNote('');
        setIsAddingNote(false);
    };

    const handleAddTag = () => {
        const value = tagInput.trim();
        if (!value) return;
        const current = detail?.tags || [];
        if (!current.includes(value)) {
            const next = [...current, value];
            onUpdateTags(next);
            setDetail((d) => (d ? { ...d, tags: next } : d));
        }
        setTagInput('');
        setIsAddingTag(false);
    };

    const handleRemoveTag = (tag: string) => {
        const next = (detail?.tags || []).filter((tg) => tg !== tag);
        onUpdateTags(next);
        setDetail((d) => (d ? { ...d, tags: next } : d));
    };

    // Same endpoint + optimistic-then-persist pattern the CRM grid uses (LeadsGrid.applyEdit) — one
    // write path for clientStatus/qualificationLevel, whichever surface the agent edits from.
    const applyStatusEdit = async (key: 'clientStatus' | 'qualificationLevel', value: string | null) => {
        if (!detail) return;
        const prev = detail[key];
        setDetail((d) => (d ? { ...d, [key]: value } : d));
        setEditingAttr(null);
        try {
            const res = await fetch(`/api/customers/${detail.leadId}/status`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ [key]: value }),
            });
            if (!res.ok) throw new Error(String(res.status));
        } catch {
            setDetail((d) => (d ? { ...d, [key]: prev } : d));
        }
    };

    // R-ID6 (catalog F-35, hard requirement): a merge that changes a lead's listed-state is SHOWN to
    // the agent + ONE-CLICK reversible. Refetches the full detail on success rather than hand-patching
    // mergeLog[].undone/qualified locally — undo can touch both, and this is a rare, deliberate action
    // where a correctness-over-snappiness re-fetch is the right trade.
    const handleUndoMerge = async () => {
        if (!detail || isUndoingMerge) return;
        setIsUndoingMerge(true);
        try {
            const res = await fetch(`/api/customers/${detail.leadId}/undo-merge`, { method: 'POST' });
            if (res.ok) {
                const fresh = await fetch(`/api/customers/${encodeURIComponent(personId)}/detail`);
                if (fresh.ok) setDetail(await fresh.json());
            }
        } finally {
            setIsUndoingMerge(false);
        }
    };

    // A UUID exists only to be copied — support shouldn't have to hand-select 36 characters.
    const handleCopyId = async () => {
        if (!detail) return;
        await navigator.clipboard.writeText(detail.leadId);
        setCopiedId(true);
        setTimeout(() => setCopiedId(false), 1500);
    };

    // #62 Contacts depth spec §3 DELETE — "Block if the contact has deals (message + a path to
    // reassign them)." The block is server-authoritative (deal count is read fresh, not from possibly-
    // stale `detail.deals`), surfaced as a dedicated error state rather than a toast, since it needs a
    // path forward (see the deal list already rendered below), not just a dismissable notice.
    const [deleteBlocked, setDeleteBlocked] = useState<{ kind: 'deals' | 'invoices'; count: number } | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    // ConfirmDialog, never window.confirm (P7 — this was the site the census missed, the sixth).
    // The copy tells the TRUTH about the verb: the endpoint suppresses (removeLead) — the record and
    // its conversation history survive and an explicit save restores them. The old key claimed
    // "can't be undone", which was false. Whether this flow should become undo-not-confirm entirely
    // (reversible ⇒ undo) is DN-4's vocabulary ruling — not decided here; only the dialog and the
    // honesty of its copy change in this pass.
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const handleDeleteContact = async () => {
        if (!detail || isDeleting) return;
        setConfirmingDelete(false);
        setIsDeleting(true);
        setDeleteBlocked(null);
        try {
            const res = await fetch(`/api/customers/${detail.leadId}`, { method: 'DELETE' });
            if (res.status === 409) {
                // B3/AC-INV2: two sibling blocks share the 409 — deals and invoices. The copy must
                // name the actual blocker, not render the deals message with a zero count. (Named
                // `refusal`, not `body`: field-reachability reads `body.<key> ==` as a body write.)
                const refusal = await res.json();
                setDeleteBlocked(refusal.error === 'blocked_by_invoices'
                    ? { kind: 'invoices', count: refusal.invoiceCount ?? 0 }
                    : { kind: 'deals', count: refusal.dealCount ?? 0 });
                return;
            }
            if (res.ok) onDeleted?.();
        } finally {
            setIsDeleting(false);
        }
    };

    const online = !!visitor;
    const displayName = detail?.firstName || detail?.lastName
        ? `${detail?.firstName || ''} ${detail?.lastName || ''}`.trim()
        : detail?.name || chat.customer;
    const channelLabel = chat.channel === 'whatsapp' ? 'WhatsApp' : chat.channel === 'web' ? 'Web' : chat.channel;
    // #50 fix: keyed on `detail.leadId` (falls back to personId only pre-first-load, when nothing else
    // is showing anyway), NOT the raw `personId` prop — during a revalidation, personId is already B
    // while `detail` (and displayName, above) are still A's; colouring the avatar from personId alone
    // would show B's colour wrapped around A's initial, a real cross-entity mismatch this fix would
    // otherwise introduce.
    const { bg: avatarBg, fg: avatarFg } = optClasses(optionToken(detail?.leadId ?? personId));

    return (
        <div className="flex flex-col h-full w-full overflow-y-auto bg-canvas" data-testid="lead-detail-panel">
            {/* pb-16 md:pb-4: the mobile BottomBar is `fixed z-50` (Nav.tsx) — ABOVE this panel's own
                `z-40` full-screen overlay — and reserves no space for itself, so without this the last
                ~44px of scrolled content sits behind it, unreachable. Same established fix as the
                conversations tab body (agent-console.tsx `pb-16 md:pb-0`), applied here where it was
                missing — measured, not assumed: scrollHeight/clientHeight prove the scroll region
                itself is correct (no min-h-0 defect); this is a distinct z-index/safe-area gap. */}
            <div className="flex flex-col gap-4 p-4 pb-16 md:pb-4">
                {loadState === 'loading' && !detail && <PanelSkeleton />}

                {loadState === 'error' && !detail && (
                    <div className="rounded-xl border border-border-neutral bg-surface p-6 text-center" data-testid="lead-detail-error">
                        <p className="text-sm text-text-muted mb-3">{t('customers.detail.loadError')}</p>
                        <button type="button" onClick={() => setRetryTick((n) => n + 1)}
                            className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary-dark">
                            {t('customers.detail.retry')}
                        </button>
                    </div>
                )}

                {detail && (
                    <div
                        // #50 fix: `detail` is now NEVER stale-and-wrong — while `loadState==='loading'`
                        // it's either the true first load (whole block not yet reached) or the
                        // `emptyDetail(personId)` shell (every field honestly absent, stamped with the
                        // CURRENT id). So this wrapper no longer needs to VISUALLY hide anything (nothing
                        // wrong is showing) — it stays fully readable, just marked busy for a11y/tests.
                        // The one residual risk (a WRITE submitted against blank fields mid-revalidation,
                        // e.g. Save while the form was already open from the previous person) is guarded
                        // per-button below (`disabled={loadState==='loading'}`), not by blocking all
                        // interaction — closing the panel or picking a different row must stay reachable.
                        data-testid="lead-detail-content"
                        aria-busy={loadState === 'loading'}
                    >
                        {loadState === 'loading' && (
                            <div data-testid="lead-detail-revalidating" className="text-xs text-text-muted text-center py-1" aria-live="polite">
                                {t('customers.detail.loading')}
                            </div>
                        )}
                        {/* Identity header (§3.1) — avatar, name, one-line secondary. No CRM/contact content
                            here: those are their own blocks below (design §3). */}
                        <div className="rounded-xl border border-border-neutral bg-surface p-4 flex items-center gap-3" data-testid="lead-detail-header">
                            <span className={`w-12 h-12 shrink-0 rounded-xl inline-flex items-center justify-center text-lg font-bold ${avatarBg} ${avatarFg}`}>
                                {(displayName || '·').substring(0, 1).toUpperCase()}
                            </span>
                            <div className="min-w-0 flex-1">
                                <h2 data-testid="lead-detail-name" className="text-lg font-bold text-text-strong truncate">{displayName}</h2>
                                <div className="flex items-center gap-1.5 text-xs text-text-muted mt-0.5 flex-wrap">
                                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${online ? 'bg-success' : 'bg-border-neutral'}`} aria-hidden />
                                    <span>{online ? t('customers.detail.online') : t('customers.detail.offline')}</span>
                                    <span aria-hidden>·</span>
                                    <span>{channelLabel}</span>
                                    {detail.firstSeenAt && (
                                        <>
                                            <span aria-hidden>·</span>
                                            <span data-testid="lead-detail-firstseen">{t('customers.detail.leadSince', { date: formatMonthYear(detail.firstSeenAt) })}</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* FROZEN — FINAL (cmdr, 2026-08-17T19:58:05Z, supersedes every earlier
                            arrangement message on this block; do not change without a fresh instruction).
                            AC-D6b — a Lead that is BOTH merged AND suppressed shows BOTH markers in ONE
                            ROW, suppressed FIRST then the merge marker — neither signal lost (UXeng's
                            preferred arrangement). Two independent conditions, so all four combinations
                            (neither/suppressed-only/merged-only/both) fall out of the same two guards
                            without a third branch. (#49 fast-follow adds the conformance test + fixture
                            for this — the arrangement itself is built here.)
                            AC-D6: suppressed marker present ONLY when suppressed, absent when active.
                            R-ID6 (catalog F-35, hard requirement): a merge that changes the lead's
                            listed-state stays SHOWN (never behind the collapsed Provenance toggle) and
                            ONE-CLICK reversible — the marker IS the undo, clicking fires handleUndoMerge
                            directly, no second step. "Collapse the detail, keep the signal" (cmdr) — the
                            broughtIn/listed-before-after DETAIL lives in the collapsed Provenance
                            accordion below; this stays a compact marker, matching the suppressed marker's
                            weight. `data-testid="lead-merge-disclosure"` is the R-ID6 conformance hook
                            (lead_merge_disclosure.cy.ts) — it names the VISIBLE element, not a
                            detail-panel wrapper. Copy is TRUTHFUL to what undo actually does (cmdr):
                            "restore listing", never "undo merge" — undo restores the listed-state, it
                            does NOT separate the merged records. */}
                        {(detail.suppressedAt || detail.mergeLog.filter((m) => !m.undone).length > 0) && (
                            <div className="flex items-center gap-2 flex-wrap">
                                {detail.suppressedAt && (
                                    <SystemChip testId="lead-suppressed-marker" tone="muted" label={t('customers.detail.suppressed')}
                                        title={t('customers.detail.suppressedOn', { date: formatDate(detail.suppressedAt) })} />
                                )}
                                {detail.mergeLog.filter((m) => !m.undone).length > 0 && (
                                    <ActionChip
                                        testId="lead-merge-disclosure"
                                        label={t('customers.detail.mergeRestoreListing')}
                                        title={t('customers.detail.mergeRestoreListingHint') as string}
                                        onAct={handleUndoMerge}
                                        disabled={isUndoingMerge}
                                    />
                                )}
                            </div>
                        )}

                        {/* B5 (D3) — the convert verb on the detail, a SIBLING row of the frozen
                            marker block above (an action, not a merge/suppress marker). Renders only
                            while the person is still a lead; the durable refetch (retryTick) is what
                            flips this panel to its contact shape — never an optimistic kind. */}
                        {detail.kind !== 'contact' && (
                            <div className="flex items-center gap-2 flex-wrap">
                                <ActionChip
                                    testId="lead-convert-detail"
                                    label={t('customers.detail.convert')}
                                    onAct={() => {
                                        setConvertFailed(false);
                                        void fetch(`/api/customers/${encodeURIComponent(detail.leadId)}/convert`, { method: 'POST' })
                                            .then((res) => {
                                                if (!res.ok) { setConvertFailed(true); return; }
                                                setRetryTick((n) => n + 1);
                                                onConverted?.();
                                            })
                                            .catch(() => setConvertFailed(true));
                                    }}
                                />
                                {convertFailed && (
                                    <span className="text-xs text-opt-rose" data-testid="lead-convert-error">{t('writeStates.actionFailed')}</span>
                                )}
                            </div>
                        )}

                        {/* CRM state (§3.2) — editable here, same write path as the grid (PUT /api/customers/:id/status). */}
                        <Card title={t('customers.detail.crmState')} testId="lead-detail-crm-state">
                            <div className="flex flex-wrap items-center gap-2 mb-3">
                                <AttributePill value={detail.clientStatus} label={t('leadsGrid.columns.clientStatus')}
                                    emptyText={t('customers.detail.setField', { field: t('leadsGrid.columns.clientStatus') })}
                                    onOpen={() => { if (loadState !== 'loading') setEditingAttr('clientStatus'); }} />
                                <AttributePill value={detail.qualificationLevel} label={t('leadsGrid.columns.qualification')}
                                    emptyText={t('customers.detail.setField', { field: t('leadsGrid.columns.qualification') })}
                                    onOpen={() => { if (loadState !== 'loading') setEditingAttr('qualificationLevel'); }} />
                            </div>
                            <Row label={t('customers.detail.qualifiedLabel')} testId="lead-detail-qualified">
                                {detail.qualified
                                    ? t('customers.detail.qualifiedVia', {
                                        reason: t(`customers.detail.reasons.${detail.qualifiedReason ?? 'unknown'}`, t('customers.detail.reasons.unknown')),
                                        date: formatDate(detail.qualifiedAt),
                                    })
                                    : t('customers.detail.notQualifiedYet')}
                            </Row>
                        </Card>
                        {editingAttr && (
                            <ValueSheet
                                shape="sheet"
                                title={`${t(editingAttr === 'clientStatus' ? 'leadsGrid.columns.clientStatus' : 'leadsGrid.columns.qualification')} — ${displayName}`}
                                options={[]}
                                current={detail[editingAttr] ?? null}
                                allowCreate
                                onSelect={(v) => applyStatusEdit(editingAttr, v)}
                                onClose={() => setEditingAttr(null)}
                            />
                        )}

                        {/* Contact (§3.3) */}
                        <Card title={t('customers.detail.contact')} icon={<User className="w-4 h-4" />} testId="lead-detail-contact-card"
                            action={!isEditingContact && (
                                <button type="button" data-testid="lead-contact-edit" onClick={() => setIsEditingContact(true)}
                                    className="text-text-muted hover:text-primary p-1 rounded-lg" title={t('customers.detail.editContact') as string}>
                                    <Edit2 className="w-4 h-4" />
                                </button>
                            )}>
                            {isEditingContact ? (
                                <div className="space-y-3">
                                    {/* min-w-0 on every grid item: an <input>'s intrinsic content width is a CSS
                                        grid item's default min-width, so without it a narrow track (a 2-col grid
                                        at 390px) doesn't shrink the input — it overflows the panel instead
                                        (measured: 583px input in a 374px panel). Same family as the flexbox
                                        min-height:auto gotcha, grid's min-width analogue. */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-[11px] font-medium text-text-muted mb-1">{t('customers.detail.firstName')}</label>
                                            <input data-testid="lead-contact-input-firstname" type="text"
                                                className="w-full text-sm p-2 bg-canvas border border-border-neutral rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none text-text-strong"
                                                value={contactForm.firstName} onChange={(e) => setContactForm({ ...contactForm, firstName: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-medium text-text-muted mb-1">{t('customers.detail.lastName')}</label>
                                            <input data-testid="lead-contact-input-lastname" type="text"
                                                className="w-full text-sm p-2 bg-canvas border border-border-neutral rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none text-text-strong"
                                                value={contactForm.lastName} onChange={(e) => setContactForm({ ...contactForm, lastName: e.target.value })} />
                                        </div>
                                        <div className="col-span-2">
                                            <label className="block text-[11px] font-medium text-text-muted mb-1">{t('customers.detail.email')}</label>
                                            <input data-testid="lead-contact-input-email" type="email"
                                                className="w-full text-sm p-2 bg-canvas border border-border-neutral rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none text-text-strong"
                                                value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} />
                                        </div>
                                        <div className="col-span-2">
                                            <label className="block text-[11px] font-medium text-text-muted mb-1">{t('customers.detail.phone')}</label>
                                            <input data-testid="lead-contact-input-phone" type="tel"
                                                className="w-full text-sm p-2 bg-canvas border border-border-neutral rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none text-text-strong"
                                                value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="flex justify-end gap-2">
                                        <button onClick={() => setIsEditingContact(false)} className="px-3 py-1.5 text-sm font-semibold text-text-muted hover:bg-canvas rounded-lg">
                                            {t('customers.detail.cancel')}
                                        </button>
                                        <button data-testid="lead-contact-save" onClick={handleContactSave} disabled={loadState === 'loading'}
                                            className="px-3 py-1.5 text-sm font-semibold bg-primary text-white hover:bg-primary-dark rounded-lg disabled:opacity-50">
                                            {t('customers.detail.save')}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <Row label={t('customers.detail.email')}><span data-testid="lead-detail-email">{detail.email || '—'}</span></Row>
                                    <Row label={t('customers.detail.phone')}><span data-testid="lead-detail-phone">{detail.phone || '—'}</span></Row>
                                    {detail.alternateContacts.length > 0 && (
                                        <div className="pt-2 mt-1 border-t border-border-neutral" data-testid="lead-alt-contacts">
                                            <div className="text-[11px] font-medium text-text-muted mb-1">{t('customers.detail.otherContact')}</div>
                                            {detail.alternateContacts.map((c, i) => (
                                                <div key={i} className="text-xs text-text-strong capitalize break-words">{c.kind}: <span className="font-medium">{c.value}</span></div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </Card>

                        {/* Tags (§3.4) — E-5: slate only, never colour-hashed. */}
                        <Card title={t('leadsGrid.columns.tags')} icon={<Tag className="w-4 h-4" />} testId="lead-detail-tags-card">
                            <div data-testid="lead-detail-tags" className="flex flex-wrap gap-2 mb-3">
                                {detail.tags.length > 0 ? (
                                    detail.tags.map((tag) => (
                                        <TagPill key={tag} tag={tag} onRemove={handleRemoveTag} testId="lead-detail-tag" />
                                    ))
                                ) : (
                                    <div className="text-xs text-text-muted italic">{t('customers.detail.tagsEmpty')}</div>
                                )}
                            </div>
                            {isAddingTag ? (
                                <div className="flex items-center gap-2">
                                    <input data-testid="lead-tag-input" autoFocus type="text" placeholder={t('customers.detail.tagPlaceholder') as string}
                                        className="flex-1 min-w-0 text-sm p-2 bg-canvas border border-border-neutral rounded-lg focus:ring-1 focus:ring-primary outline-none text-text-strong"
                                        value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAddTag()} />
                                    <button data-testid="lead-tag-add" onClick={handleAddTag} disabled={loadState === 'loading'}
                                        className="p-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50">
                                        <Plus className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => setIsAddingTag(false)} className="p-2 text-text-muted hover:bg-canvas rounded-lg">
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <button data-testid="lead-tag-add-open" onClick={() => setIsAddingTag(true)}
                                    className="w-full py-2 border border-dashed border-border-neutral rounded-lg text-xs font-semibold text-text-muted hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-1.5">
                                    <Plus className="w-3 h-3" /> {t('customers.detail.addTag')}
                                </button>
                            )}
                        </Card>

                        {/* Custom fields (§5) — EMPTY-FIRST: an account with zero field defs renders nothing
                            at all (detail.customFields.length is the DEF count, not the value count — a def
                            with no value for this lead still shows a "—" row, which is correct: the section
                            exists, this one field is empty-but-correct). Progressive disclosure, no hard cap. */}
                        {detail.customFields.length > 0 && (
                            <Card title={t('customers.detail.customFields')} testId="lead-detail-custom-fields">
                                <div>
                                    {(showAllFields ? detail.customFields : detail.customFields.slice(0, FIELD_BUDGET)).map((f) => (
                                        <Row key={f.fieldDefId} label={f.label} testId="lead-detail-custom-field">{f.value || '—'}</Row>
                                    ))}
                                </div>
                                {detail.customFields.length > FIELD_BUDGET && !showAllFields && (
                                    <button type="button" data-testid="lead-custom-fields-more" onClick={() => setShowAllFields(true)}
                                        className="mt-2 text-xs font-semibold text-primary hover:underline">
                                        {t('customers.detail.showMore', { count: detail.customFields.length - FIELD_BUDGET })}
                                    </button>
                                )}
                            </Card>
                        )}

                        {/* #62 Contacts depth spec §3 DETAIL — "plus the new relations: company · deals ·
                            tasks · activity timeline." Display-only this round (no picker to CHANGE
                            company/link a deal yet — same "field exists, setter UI ships later" pattern
                            as Company.primaryContactId/Account.dealStalenessThresholdDays). */}
                        <Card title={t('customers.detail.company')} icon={<Building2 className="w-4 h-4" />} testId="lead-detail-company-card">
                            <span data-testid="lead-detail-company">{detail.company ? detail.company.name : <span className="text-text-muted italic">{t('customers.detail.noCompany')}</span>}</span>
                        </Card>

                        <Card title={t('customers.detail.deals')} icon={<DollarSign className="w-4 h-4" />} testId="lead-detail-deals-card">
                            {detail.deals && detail.deals.length > 0 ? (
                                <div className="space-y-1.5" data-testid="lead-detail-deals-list">
                                    {detail.deals.map((d) => (
                                        <div key={d.id} className="flex items-center justify-between text-sm gap-2">
                                            <span className="truncate text-text-strong">{d.name}</span>
                                            <span className="text-xs text-text-muted shrink-0">{d.stageName ?? '—'}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <span className="text-text-muted italic text-sm">{t('customers.detail.noDeals')}</span>
                            )}
                        </Card>

                        <Card title={t('customers.detail.tasks')} icon={<ListChecks className="w-4 h-4" />} testId="lead-detail-tasks-card">
                            {detail.tasks && detail.tasks.length > 0 ? (
                                <div className="space-y-1.5" data-testid="lead-detail-tasks-list">
                                    {detail.tasks.map((tk) => (
                                        <div key={tk.id} className={`text-sm truncate ${tk.completedAt ? 'line-through text-text-muted' : 'text-text-strong'}`}>{tk.title}</div>
                                    ))}
                                </div>
                            ) : (
                                <span className="text-text-muted italic text-sm">{t('customers.detail.noTasks')}</span>
                            )}
                        </Card>

                        <Card title={t('customers.detail.recordHistory')} icon={<Clock className="w-4 h-4" />} testId="lead-detail-timeline-card">
                            {detail.recentActivity && detail.recentActivity.length > 0 ? (
                                <div className="space-y-2" data-testid="lead-detail-timeline-list">
                                    {detail.recentActivity.map((a) => (
                                        <div key={a.id} className="text-sm">
                                            <div className="text-text-strong">{a.summary}</div>
                                            <div className="text-[11px] text-text-muted">{formatDate(a.occurredAt)}</div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <span className="text-text-muted italic text-sm">{t('customers.detail.noChangesYet')}</span>
                            )}
                        </Card>

                        {/* Notes (§3.6) */}
                        <Card title={t('customers.detail.activity')} icon={<FileText className="w-4 h-4" />} testId="lead-detail-notes-card">
                            <div className="flex items-start gap-2 mb-3">
                                <textarea data-testid="lead-note-input"
                                    className="flex-1 text-sm p-2.5 bg-canvas border border-border-neutral rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none resize-none text-text-strong"
                                    placeholder={t('customers.detail.notePlaceholder') as string} rows={2} value={newNote} onChange={(e) => setNewNote(e.target.value)} />
                                <button data-testid="lead-note-add" onClick={handleNoteSubmit} disabled={isAddingNote || !newNote.trim()}
                                    className="p-2.5 bg-primary text-white hover:bg-primary-dark rounded-lg disabled:opacity-50 shrink-0">
                                    <Plus className="w-4 h-4" />
                                </button>
                            </div>
                            <div data-testid="lead-detail-notes" className="space-y-2 max-h-[280px] overflow-y-auto pe-1">
                                {notes && notes.length > 0 ? (
                                    notes.map((note) => (
                                        <div key={note.id} data-testid="lead-detail-note" className="rounded-lg border border-border-neutral bg-canvas p-3">
                                            <div className="flex justify-between items-center mb-1 gap-2">
                                                <span className="text-xs font-semibold text-text-strong truncate">{note.agentName}</span>
                                                <span className="text-[11px] text-text-muted shrink-0">{formatDate(note.createdAt)}</span>
                                            </div>
                                            <p className="text-sm text-text-strong">{note.content}</p>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-xs text-text-muted italic text-center py-4">{t('customers.detail.notesEmpty')}</div>
                                )}
                            </div>
                        </Card>

                        {/* Past conversations (§3.7) */}
                        <Card title={t('customers.previousConversations')} icon={<MessageCircle className="w-4 h-4" />} testId="lead-detail-conversations-card">
                            {detail.conversationsCount > 0 && (
                                <div className="text-xs text-text-muted mb-2">
                                    {t('customers.detail.conversationsCount', { count: detail.conversationsCount })}
                                    {detail.lastConversationAt ? ` · ${t('customers.detail.lastConversation', { date: formatDate(detail.lastConversationAt) })}` : ''}
                                </div>
                            )}
                            {chat.previousChats && chat.previousChats.length > 0 ? (
                                <div className="space-y-2">
                                    {chat.previousChats.map((prevChat, index) => (
                                        <div key={`chat-${index}`} onClick={() => onViewPastChat(prevChat)}
                                            className="rounded-lg border border-border-neutral bg-canvas p-3 cursor-pointer hover:border-primary transition-colors">
                                            <div className="flex justify-between items-center mb-1 gap-2">
                                                <span className="text-xs font-semibold text-text-strong truncate">{prevChat.topic}</span>
                                                <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold shrink-0 ${prevChat.status === 'resolved' ? 'bg-opt-emerald-bg text-opt-emerald' : 'bg-opt-amber-bg text-opt-amber'}`}>
                                                    {prevChat.status}
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center text-[11px] text-text-muted">
                                                <span>{formatDate(prevChat.date)}</span>
                                                <span>{t('customers.detail.viewChat')}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-xs text-text-muted italic text-center py-4">{t('customers.noConversations')}</div>
                            )}
                        </Card>

                        {/* Session (§3.8) — THREE classes, not two (PM's AC1 ruling):
                            1. LAST-KNOWN (location, device/OS) — persisted on the Lead cumulatively
                               (LeadService.enrich never clears an earlier value), so they OUTLIVE the
                               session. Shown ONLINE OR OFFLINE, sourced online from the live `visitor`
                               (freshest), offline from `detail.location`/`detail.userAgent` (the durable
                               snapshot) — hedged "Last known: …" and UNDATED whenever it's the fallback,
                               so it never reads as a current fact.
                            2. LIVE-ONLY (IP, current page, navigation history) — genuinely absent offline
                               (no durable column holds them) — absent-with-explanation, never a dash. */}
                        <Card title={t('customers.detail.session')} icon={<Monitor className="w-4 h-4" />} testId="lead-detail-session-card">
                            {(() => {
                                const loc = visitor?.location ?? detail.location;
                                const ua = visitor?.userAgent ?? detail.userAgent;
                                const locationText = loc ? `${loc.city ?? ''}${loc.city && loc.countryCode ? ', ' : ''}${loc.countryCode ?? ''}`.trim() : null;
                                const deviceText = ua ? `${ua.browser ?? ''} ${ua.version ?? ''}`.trim() : null;
                                const osText = ua && (ua.os || ua.platform) ? `${ua.os ?? ''}${ua.os && ua.platform ? ' (' : ''}${ua.platform ?? ''}${ua.os && ua.platform ? ')' : ''}` : null;
                                const hedge = (v: string | null) => (v ? (online ? v : t('customers.detail.lastKnown', { value: v }) as string) : '—');
                                return (
                                    <div>
                                        <Row label={t('customers.detail.location')} testId="lead-detail-location-row">
                                            <span data-testid="lead-detail-location">{hedge(locationText)}</span>
                                        </Row>
                                        <Row label={t('customers.detail.browser')} testId="lead-detail-device-row">
                                            <span data-testid="lead-detail-device">{hedge(deviceText)}</span>
                                        </Row>
                                        {osText && <Row label={t('customers.detail.os')}>{online ? osText : t('customers.detail.lastKnown', { value: osText })}</Row>}
                                        {online && (
                                            <>
                                                <Row label={t('customers.detail.ip')}>{visitor?.ip || '—'}</Row>
                                                {visitor?.navigationHistory && visitor.navigationHistory.length > 0 && (
                                                    <div className="pt-2">
                                                        <div className="text-[11px] font-medium text-text-muted mb-1">{t('customers.detail.recentPages')}</div>
                                                        <div className="space-y-1 max-h-28 overflow-y-auto">
                                                            {visitor.navigationHistory.slice(-4).reverse().map((visit, i) => (
                                                                <div key={i} className="bg-canvas p-1.5 rounded-lg border border-border-neutral text-[11px]">
                                                                    <div className="font-semibold text-text-strong truncate">{visit.path}</div>
                                                                    <div className="text-text-muted">{visit.timeSpent}</div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        {!online && (
                                            <div className="text-[11px] text-text-muted italic mt-2 pt-2 border-t border-border-neutral" data-testid="lead-detail-offline-note">
                                                {t('customers.detail.offlineNote')}
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        </Card>

                        {/* Provenance & history (§3.9) — collapsed by default; the DETAIL (cmdr: "collapse
                            the detail, keep the signal"): embedOrigin/save/suppress records + the full
                            merge-log breakdown (broughtIn counts, listed-before/after), for EVERY entry —
                            active and already-undone alike. The compact marker above is the R-ID6 SIGNAL
                            (always visible, one-click undo); this is where an agent reads what a merge
                            actually brought in, which is exactly the audit reading cmdr scoped here. */}
                        <div className="rounded-xl border border-border-neutral bg-surface" data-testid="lead-detail-provenance">
                            <button type="button" onClick={() => setShowProvenance((s) => !s)} className="w-full flex items-center justify-between p-4">
                                <div className="flex items-center gap-2">
                                    <HistoryIcon className="w-4 h-4 text-text-muted" />
                                    <h3 className="text-sm font-semibold text-text-strong">{t('customers.detail.provenance')}</h3>
                                </div>
                                <ChevronDown className={`w-4 h-4 text-text-muted transition-transform ${showProvenance ? 'rotate-180' : ''}`} />
                            </button>
                            {showProvenance && (
                                <div className="px-4 pb-4 border-t border-border-neutral pt-3">
                                    {detail.embedOrigin && <Row label={t('customers.detail.cameFrom')}>{detail.embedOrigin}</Row>}
                                    {detail.savedAt && <Row label={t('customers.detail.savedLabel')}>{formatDate(detail.savedAt)}</Row>}
                                    {detail.suppressedAt && <Row label={t('customers.detail.suppressedOnLabel')}>{formatDate(detail.suppressedAt)}</Row>}
                                    {detail.mergeLog.length > 0 && (
                                        <div className="pt-1">
                                            <div className="text-[11px] font-medium text-text-muted mb-1">{t('customers.detail.mergeHistory')}</div>
                                            {detail.mergeLog.map((m, i) => (
                                                <div key={i} className="text-[11px] text-text-muted">
                                                    {t('customers.detail.broughtIn', { conversations: m.broughtIn?.conversations ?? 0, tags: m.broughtIn?.tags ?? 0 })}
                                                    {m.listedBefore !== m.listedAfter ? ` ${m.listedAfter ? t('customers.detail.nowListed') : t('customers.detail.nowUnlisted')}` : ''}
                                                    {m.undone ? ` · ${t('customers.detail.undone')}` : ''}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {/* AC-D11 — the lead reference is NOT provenance data (not an event); it's
                                        ALWAYS present, so it renders UNCONDITIONALLY here, regardless of
                                        whether embedOrigin/savedAt/suppressedAt/mergeLog have anything —
                                        never gated by that guard, else a brand-new lead (F-NEW, zero events)
                                        would lose support's reference affordance entirely. The old
                                        "no provenance recorded yet" empty-message is dropped: once this
                                        footer always has content, showing "nothing here" beside an ID row
                                        would itself be the contradiction cmdr's ruling forbids. */}
                                    <div className="pt-2 mt-1 border-t border-border-neutral flex items-center gap-1.5">
                                        <span className="text-[11px] font-medium text-text-muted shrink-0">{t('customers.detail.internalId')}</span>
                                        <span className="font-mono text-xs text-text-muted truncate min-w-0 flex-1" title={detail.leadId} data-testid="lead-detail-internal-id">
                                            {detail.leadId}
                                        </span>
                                        <button type="button" onClick={handleCopyId} title={t(copiedId ? 'customers.detail.copied' : 'customers.detail.copyId')}
                                            className="shrink-0 p-1 rounded hover:bg-canvas text-text-muted">
                                            {copiedId ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* #62 Contacts depth spec §3 DELETE — separated, text-opt-rose, never a filled
                            button (same ratified shape as Task delete). Block if deals attached; asks
                            first via the ratified ConfirmDialog (the removeLead suppression it calls IS
                            durably reversible via save, but that is not obvious at the button, so this
                            still asks — and the dialog copy now states the suppression truthfully). */}
                        {onDeleted && (
                            <div className="pt-2 mt-2 border-t border-border-neutral">
                                {deleteBlocked !== null && (
                                    <div className="text-xs text-opt-rose mb-2" data-testid="lead-delete-blocked">
                                        {t(deleteBlocked.kind === 'invoices' ? 'customers.detail.deleteBlockedByInvoices' : 'customers.detail.deleteBlockedByDeals', { count: deleteBlocked.count })}
                                    </div>
                                )}
                                <button type="button" data-testid="lead-delete" onClick={() => setConfirmingDelete(true)} disabled={isDeleting}
                                    className="text-sm font-semibold min-h-[44px] px-1 text-opt-rose disabled:opacity-50">
                                    {t('customers.detail.delete')}
                                </button>
                            </div>
                        )}

                        {confirmingDelete && detail && (
                            <ConfirmDialog destructive
                                message={t('customers.detail.removeConfirm', { name: displayName })}
                                confirmLabel={t('customers.detail.delete')}
                                onConfirm={() => void handleDeleteContact()}
                                onClose={() => setConfirmingDelete(false)} />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
